import { Controller } from '@hotwired/stimulus'
import { render } from 'nunjucks'
import TabsSlider from 'tabs-slider'
import PullToRefresh from 'pulltorefreshjs'
import { Grid, html } from 'gridjs'
import { esES } from 'gridjs/l10n'

import { router } from '../router'

import {Events, Strings} from '../constants'

import {
    target,
    sendEvent,
    decodeJson,
    subscribeEvent,
    unsubscribeEvent,
    retrieveSession,
    retrieveState,
    updateState,
    updateTmp,
    setText,
    notifier,
    createParamsFromToSend, encodeJson
} from '../utils'

import {historyPostRequest, saveNotificationPostRequest} from '../api'

export default class extends Controller {

    static targets = [
        'container',
        'tabs',
        'total-registered',
        'total-to-send',
        'registered',
        'to-send',
    ]

    static values = {
    }

    state = {
        session: null,
        registered: [],
        toSend: []
    }

    instances = {
        ptr: null,
        tabs: null,
        tables: {
            registered: null,
            toSend: null
        }
    }

    configs = {
        tabs: {
            animate: true,
            slide: 0,
            rtl: false,
            draggable: true,
            underline: true,
            heightAnimate: true,
            duration: 500,
            easing: 'cubic-bezier(0.0, 0.0, 0.2, 1)'
        },
        tables: {
            language: esES,
            search: {
                selector: (cell) => encodeJson(cell)
            },
            fixedHeader: true,
            height: 'calc(100vh - 220px)',
            columns: [
                {
                    id: 'Notificación',
                    data: (data) => data,
                    formatter: (notification) => html(
                        render('my-notification.html', notification)
                    )
                }
            ],
            style: {
                header: {
                    'text-align': 'center'
                },
                th: {
                    display: 'none'
                }
            },
            data: []
        }
    }

    container = () => target(this, 'container')

    tabs = () => target(this, 'tabs')

    totalRegistered = () => target(this, 'total-registered')

    totalToSend = () => target(this, 'total-to-send')

    registered = () => target(this, 'registered')

    toSend = () => target(this, 'to-send')

    initialize = () => {

        this.configs.tables.language.search.placeholder = 'Buscar Notificación...'
        this.configs.tables.language.noRecordsFound = 'Ningún resultado encontrado...'

        this.instances.tabs = new TabsSlider(this.tabs(), {
            ...this.configs.tabs
        })

        this.instances.tables.registered = new Grid({
            ...this.configs.tables
        })

        this.instances.tables.registered.on('rowClick', this.registeredSelected)

        this.instances.tables.toSend = new Grid({
            ...this.configs.tables
        })

        this.instances.tables.toSend.on('rowClick', this.toSendSelected)

        this.renderTables()

        this.renderPtr()
    }

    connect = () => {

        this.refreshOnLogin()

        this.fixInputs()

        subscribeEvent(Events.ON_LOGIN, this.refreshOnLogin)
    }

    disconnect = () => {

        this.unfixInputs()

        unsubscribeEvent(Events.ON_LOGIN, this.refreshOnLogin)
    }

    refreshOnLogin = () => {

        retrieveSession()
            .then((session) => {

                if (! session) {

                    sendEvent(Events.SHOW_LOGIN, { message: '' })

                    return
                }

                this.state.session = session

                return retrieveState()
                    .then((state) => {

                        if (! state || ! state.toSend) {

                            state = state || {}

                            state.toSend = []
                        }

                        this.state.toSend = state.toSend

                        this.instances.tables.toSend.updateConfig({ data: this.state.toSend })

                        if (! state || ! state.registered) {

                            this.renderTables(true)

                            this.refresh()

                            return
                        }

                        this.state.registered = state.registered

                        this.instances.tables.registered.updateConfig({ data: this.state.registered })

                        this.renderTables(true)
                    })
                    .then(() => this.fixTabs())
                    .catch(() => {})
          })
          .catch(() => {

              sendEvent(Events.SHOW_LOGIN, { message: '' })
          })
    }

    refresh = () => {

        if (! this.state.session) {

            return
        }

        const params = {
            rpe: this.state.session.rpe,
            password: this.state.session.password
        }

        historyPostRequest(params)
            .then(this.onRefresh)
            .catch(this.onRefresh)
    }

    onRefresh = (response) => {

        if (response.estado) {

            this.state.registered = decodeJson(response.mensaje)

            this.instances.tables.registered.updateConfig({ data: this.state.registered })

            this.renderTables(true)

            updateState({
                registered: this.state.registered
            })
        }
    }

    fixTabs = () => {

        setTimeout(() => this.instances.tabs.recalcStyles(), 1000)
    }

    fixInputs = () => {

        setTimeout(() => this.tabs().querySelectorAll('.gridjs-input').forEach(this.fixInput), 1000)
    }

    unfixInputs = () => {

        setTimeout(() => this.tabs().querySelectorAll('.gridjs-input').forEach(this.unfixInput), 1000)
    }

    fixInput = (input) => {

        input.addEventListener('mousedown', this.fixInputEvent)
    }

    unfixInput = (input) => {

        input.removeEventListener('mousedown', this.fixInputEvent)
    }

    fixInputEvent = (event) => {

        event.stopPropagation()
    }

    renderTables = (force = false) => {

        setText(this.totalRegistered(), this.state.registered.length)

        setText(this.totalToSend(), this.state.toSend.length)

        if (force) {

            this.instances.tables.registered.forceRender()

            this.instances.tables.toSend.forceRender()

            return
        }

        this.instances.tables.registered.render(this.registered())

        this.instances.tables.toSend.render(this.toSend())
    }

    renderPtr = () => {

        this.instances.ptr = PullToRefresh.init({
            instructionsPullToRefresh: 'Desliza hacia abajo para actualizar',
            instructionsReleaseToRefresh: 'Suelta para actualizar',
            instructionsRefreshing: 'Actualizando',
            mainElement: this.container(),
            triggerElement: this.container(),
            onRefresh: this.refresh,
            shouldPullToRefresh: () => {

                switch (this.instances.tabs.currentId) {

                    case 0: return ! this.registered().querySelector('.gridjs-wrapper').scrollTop

                    case 1: return ! this.toSend().querySelector('.gridjs-wrapper').scrollTop

                    default: return false
                }
            }
        })
    }

    registeredSelected = (_, { cells: [ { data } ] }) => {

        this.openSelected(data)
    }

    toSendSelected = (_, { cells: [ { data } ] }) => {

        notifier.info(Strings.MESSAGE_VISIT_PENDING_TO_REGISTER)
    }

    openSelected = (data) => {

        updateTmp(data)
            .then(() => {

                const notification = data.notificacion

                router.navigate(`/visita/${notification}`, {
                    historyAPIMethod: 'replaceState'
                })
            })
            .catch(() => {})
    }

    sendPending = () => {

        if (this.state.toSend.length === 0) {

            notifier.success(Strings.MESSAGE_WITHOUT_VISIT_PENDING_TO_REGISTER)

            return
        }

        sendEvent(Events.SHOW_LOADING)

        this.send(this.state.toSend[this.state.toSend.length - 1])
    }

    send = (toSend) => {

        createParamsFromToSend(toSend)
            .then((params) => {

                saveNotificationPostRequest(params)
                    .then(this.onSend)
                    .catch(this.onSend)
            })
            .catch(() => {

                sendEvent(Events.HIDE_LOADING)

                notifier.alert(Strings.ERROR_SAVING_VISIT)
            })
    }

    onSend = (response) => {

        if (! response.estado) {

            sendEvent(Events.HIDE_LOADING)

            notifier.alert(Strings.ERROR_SAVING_VISIT)

            return
        }

        this.state.toSend.pop()

        this.instances.tables.toSend.updateConfig({ data: this.state.toSend })

        this.renderTables(true)

        updateState({
            toSend: this.state.toSend
        })

        if (this.state.toSend.length === 0) {

            sendEvent(Events.HIDE_LOADING)

            notifier.success(response.mensaje)

            return
        }

        this.send(this.state.toSend[this.state.toSend.length - 1])
    }
}
